import { HDate } from '@hebcal/core';
import { DATETIME_OPTIONS, DATE_OPTIONS, DATE_OPTIONS_TRIMMED, DAY_NAMES, DAYS_IN_HEBREW, HEBREW_MONTHS, MONTHS_IN_HEBREW } from '@/utils/const';
import { formatHebrewYear } from '@/utils/hebrew_year';
import { toHebrewPickerDateInputValue, toWallClockDate } from '@/utils/wall_clock_date';

const filters = {
  firstLetter(value: string) {
    return value != null ? value[0] : '';
  },

  capitalize(value: string) {
    return value != null ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  },

  uppercase(value: string) {
    return value != null ? value.toString().toUpperCase() : '';
  },

  getDateOnly(value: string) {
    if (value == null) return '-';
    
    const date = new Date(value);
    // Use local date components to avoid timezone shifts
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return localDate.toLocaleDateString('en-US', DATE_OPTIONS);
  },

  getDateOnlyTrimmed(value: string) {
    return value != null
      ? new Date(value).toLocaleDateString('en-US', DATE_OPTIONS_TRIMMED)
      : '-';
  },

  getTimeOnly(value: string) {
    return value != null
      ? new Date(value).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '-';
  },

  convertToHebrewDate(value: string) {
    const gregDate = toWallClockDate(value);
    if (!gregDate) return '-';
    const hebrewDate = new HDate(gregDate);
      let monthIndex = HEBREW_MONTHS.indexOf(hebrewDate.getMonthName())
      if (monthIndex != -1) {
          return `${DAYS_IN_HEBREW[hebrewDate.getDate() - 1]} ${MONTHS_IN_HEBREW[monthIndex]} ${formatHebrewYear(hebrewDate.getFullYear())}`
      }
      return '-'

      // return hebrewDate.toString();
  },

  // Gregorian counterpart of convertToHebrewDate: the "Display Calendar Type"
  // setting swaps between the two. Reads the stored date as a wall-clock value
  // (same as the Hebrew label) so both calendars describe the very same day,
  // and returns '-' for empty/invalid input to match convertToHebrewDate.
  convertToGregorianDate(value: string | Date) {
    const date = toWallClockDate(value);
    if (!date) return '-';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  },

  convertToHebrewDateObject(value: string) {
    const gregDate = new Date(value);
    const hebrewDate = new HDate(gregDate);
      let monthIndex = HEBREW_MONTHS.indexOf(hebrewDate.getMonthName())
      if (monthIndex != -1) {
          return {
            dayOfWeek: DAY_NAMES[gregDate.getDay()],
            dayOfMonth: DAYS_IN_HEBREW[hebrewDate.getDate() - 1],
            month: MONTHS_IN_HEBREW[monthIndex],
            year: formatHebrewYear(hebrewDate.getFullYear())
          }
      }
      return '-'
  },

  convertToEnglishHebrewDate(value: string) {
    const gregDate = new Date(value);
    const hebrewDate = new HDate(gregDate);
    console.log('HEBREW_MONTHS.indexOf(hebrewDate.getMonthName())', hebrewDate.getMonthName(), HEBREW_MONTHS.indexOf(hebrewDate.getMonthName()))
    return `${hebrewDate.getDate()} ${hebrewDate.getMonthName()} ${hebrewDate.getFullYear()}`
  },

  // Day and transliterated month of the Hebrew date the given day falls on,
  // e.g. "15 Kislev". The month name is read off the HDate itself rather than
  // indexed out of HEBREW_MONTHS, so a leap year reads "Adar I" / "Adar II"
  // instead of sliding a month along a fixed twelve-month list. The value is
  // read as a wall-clock date, so a stored ISO instant never lands a day off.
  convertToHebrewDateDayAndMonth(value: string | Date) {
      const gregDate = toWallClockDate(value);
      if (!gregDate) return '';
      const hebrewDate = new HDate(gregDate);
      return `${hebrewDate.getDate()} ${hebrewDate.getMonthName()}`;
  },

  convertToHebrewMonthOnly(value: string) {
    const gregDate = new Date(value);
    const hebrewDate = new HDate(gregDate);
    return `${HEBREW_MONTHS[hebrewDate.getMonth() - 1]}`;
  },

  convertHDateToGregorian(hDate: HDate) {
      // Keep this as a calendar day rather than converting local midnight to
      // UTC, which can silently send the previous/next day to the backend.
      return toHebrewPickerDateInputValue(hDate);
  },

  convertHDateStringToGregorian(hebrewDateStr: String) {
      const [day, month, year] = hebrewDateStr.split(' ');
      const hebrewDate = new HDate(parseInt(day), month, parseInt(year));

      const gregorianDate = hebrewDate.greg();

      return gregorianDate;
  },


  getTimeOnly24Hrs(value: string) {
    return value != null
      ? new Date(value).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
      : '-';
  },

  getDateAndTime(value: string) {
    return value != null
      ? new Date(value).toLocaleDateString('en-US', DATETIME_OPTIONS)
      : '-';
  },

  checkDateRange(startDate: Date, endDate: Date): number {
    const now = new Date();
    if (now > endDate) {
      return 0; // Past
    } else if (now > startDate && now < endDate) {
      return 1; // Within the range
    } else if (now < startDate) {
      return 0;
    } else {
      return 0;
    }
  },

  formatDate(value: string) {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  },

  round(value: number) {
    return value.toFixed(2);
  },

  capitalizedFirstLetter(str: string) {
    if (!str) return '';
    return str.charAt(0).toUpperCase();
  },

  formatNumberWithComma(number: number) {
    if (isNaN(number)) {
      return 'Invalid number';
    }
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },
};

export default filters;
